import React from 'react';
import { Box, Button, Grid } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import SettingsCard from './SettingsCard';
import { personalInfoSchema, PersonalInfoValues } from './validation';

type PersonalInfoCardProps = {
  values: PersonalInfoValues;
  onSave: (values: PersonalInfoValues) => Promise<void>;
};

const errorAdornment = (hasError: boolean) => (hasError
  ? { endAdornment: <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} /> }
  : undefined);

function PersonalInfoCard({ values, onSave }: PersonalInfoCardProps) {
  const formContext = useForm<PersonalInfoValues>({
    defaultValues: values,
    resolver: yupResolver(personalInfoSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { reset, formState: { errors, isSubmitting } } = formContext;

  // petParent is null on first render, so seed the form once it loads.
  React.useEffect(() => {
    reset(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.firstName, values.lastName, values.phoneNumber, values.email]);

  return (
    <SettingsCard
      title="Personal Information"
      subtitle="Keep your contact details current so your shelter and trainers can reach you."
    >
      <FormContainer formContext={formContext} onSuccess={onSave}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <TextFieldElement
              name="firstName"
              label="First name"
              variant="outlined"
              fullWidth
              InputProps={errorAdornment(Boolean(errors.firstName))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldElement
              name="lastName"
              label="Last name"
              variant="outlined"
              fullWidth
              InputProps={errorAdornment(Boolean(errors.lastName))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldElement
              name="phoneNumber"
              label="Mobile phone number"
              type="tel"
              variant="outlined"
              fullWidth
              InputProps={errorAdornment(Boolean(errors.phoneNumber))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldElement
              name="email"
              label="Email address"
              type="email"
              variant="outlined"
              fullWidth
              InputProps={errorAdornment(Boolean(errors.email))}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            Save Changes
          </Button>
        </Box>
      </FormContainer>
    </SettingsCard>
  );
}

export default PersonalInfoCard;
